import { useState } from "react";
import { TENANT_DOMAIN } from "../../utils/domain";
import { ERROR_MESSAGES } from "../../constant/constant";
import { useAxios } from "../../utils/useAxios";

/**
 * @typedef {'razorpay' | 'stripe'} PaymentGateway
 */

/**
 * @typedef {Object} CreateOrderData
 * @property {number} invoice_id - Invoice ID to create payment order for
 * @property {PaymentGateway} gateway - Payment gateway (razorpay or stripe)
 * @property {string|number} amount - Amount to pay
 * @property {string} currency - Currency code (e.g., "INR", "USD")
 */

/**
 * @typedef {Object} RazorpayOrderResponse
 * @property {'razorpay'} gateway
 * @property {string} order_id - Razorpay order ID
 * @property {number} amount - Amount in smallest currency unit (paise)
 * @property {string} currency - Currency code (e.g., "INR")
 * @property {string} key - Razorpay public key
 */

/**
 * @typedef {Object} StripeOrderResponse
 * @property {'stripe'} gateway
 * @property {string} client_secret - Stripe client secret
 * @property {string} intent_id - Stripe payment intent ID
 * @property {number} amount - Amount in smallest currency unit
 * @property {string} currency - Currency code (e.g., "INR")
 * @property {string} public_key - Stripe public key
 */

/**
 * @typedef {RazorpayOrderResponse | StripeOrderResponse} PaymentOrderResponse
 */

/**
 * @description Custom hook to create a payment order for online payments (Razorpay/Stripe)
 */
export const useCreatePaymentOrder = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const [orderData, setOrderData] = useState(null);

    const { axiosInstance } = useAxios();

    /**
     * @description Create a payment order for online payment
     * @async
     * @param {CreateOrderData} orderData - The payment order data
     * @param {() => void} [onSuccess] - Callback function to be called on success
     * @returns {Promise<PaymentOrderResponse | null>} Returns payment order data on success, null on failure
     */
    const createPaymentOrder = async ({ invoice_id, gateway, amount, currency }, onSuccess) => {
        try {
            setLoading(true);
            setError("");
            setSuccess(false);
            setOrderData(null);

            const response = await axiosInstance.post(
                `${TENANT_DOMAIN}/billing/payments/online/create-order`,
                {
                    invoice_id,
                    gateway,
                    amount: String(amount),
                    currency,
                }
            );

            const data = response.data;
            setOrderData(data);
            setSuccess(true);

            if (onSuccess) onSuccess(data);

            return data;
        } catch (err) {
            setSuccess(false);
            setOrderData(null);
            if (err?.response?.status === 500 || err?.status === 500) {
                setError(ERROR_MESSAGES.INTERNAL_SERVER);
            } else {
                setError(
                    err?.response?.data?.error || ERROR_MESSAGES.UNKNOWN
                );
            }

            return null;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Reset the hook state
     */
    const reset = () => {
        setLoading(false);
        setError("");
        setSuccess(false);
        setOrderData(null);
    };

    return {
        loading,
        error,
        success,
        orderData,
        createPaymentOrder,
        reset,
    };
};

export default useCreatePaymentOrder;